import { useState, useEffect } from "react";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import ContactModal from "./ContactModal";

const navLinks = [
  { label: "HJEM", href: "/" },
  { label: "OM OSS", href: "/om-oss" },
  {
    label: "BADSTUE",
    dropdown: [
      { label: "LYKKE LY", href: "/sauna-ly" },
      { label: "LYKKE PANORAMA", href: "/sauna-panorama" },
      { label: "LYKKE FJELL", href: "/sauna-fjel" }
    ]
  },
  { label: "PROSESS", href: "/process" },
  { label: "LÆR MER", href: "/laer-mer-handverk" },
  { label: "PORTFOLIO", href: "/portfolio" },
  { label: "SERTIFIKATER", href: "/sertifikater" }
];


function Dropdown({ label, items, scrolled }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative group">
      <button className={`text-base font-medium tracking-widest-xl transition-colors duration-300 hover:opacity-60 flex items-center gap-1 ${
        scrolled ? "text-foreground" : "text-foreground"
      }`}>
        {label}
        <ChevronDown size={16} className="group-hover:rotate-180 transition-transform" />
      </button>
      <div className="absolute left-0 top-full pt-2 opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto pointer-events-none transition-opacity z-50">
        <div className="bg-background border border-border/40 rounded-lg overflow-hidden min-w-max">
          {items.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => window.location.href = item.href}
              className="block px-6 py-3 text-foreground hover:bg-secondary/30 transition-colors text-sm font-body"
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function SearchBar({ scrolled }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const searchPages = [
    { name: "Hjem", href: "/" },
    { name: "Om oss", href: "/om-oss" },
    { name: "BADSTUE LY", href: "/sauna-ly" },
    { name: "BADSTUE PANORAMA", href: "/sauna-panorama" },
    { name: "BADSTUE FJEL", href: "/sauna-fjel" },
    { name: "Lær Mer", href: "/laer-mer" },
    { name: "Sertifikater", href: "/sertifikater" },
    { name: "Prosess", href: "/process" }
  ];

  const results = searchQuery.trim()
    ? searchPages.filter((page) =>
        page.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <div className="relative hidden lg:block">
      <button
        onClick={() => setSearchOpen(!searchOpen)}
        aria-label="Søk"
        className="p-2 -m-2 transition-colors text-foreground hover:text-foreground/70"
      >
        <Search size={20} />
      </button>
      {searchOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 z-50">
          <input
            type="text"
            placeholder="Søk..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            className="w-full px-4 py-2 bg-background border border-border text-foreground placeholder-foreground/50 rounded-lg"
          />
          {results.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg overflow-hidden shadow-lg">
              {results.map((result) => (
                <a
                  key={result.href}
                  href={result.href}
                  className="block px-4 py-2 hover:bg-secondary/30 transition-colors text-sm"
                  onClick={() => setSearchOpen(false)}
                >
                  {result.name}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    window.location.href = href;
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ?
        "bg-background/95 backdrop-blur-md border-b border-border/40" :
        "bg-transparent"}`
        }>
        
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center h-28">
          <button
            onClick={() => handleNavClick("/")}
            className="transition-opacity duration-300 hover:opacity-70 flex-shrink-0 mr-auto">
            
            <img
              src="https://media.base44.com/images/public/69db395aa041ab4d87ab84d7/f42f4e4be_LYKKE_Nomad_1Colorrgb1920x.png"
              alt="LYKKE NOMAD – Premium badstuer med nordisk arkitektur"
              className={`h-16 md:h-24 w-auto transition-all duration-500 ${scrolled ? "brightness-0" : "brightness-0 invert"}`}
              width="1920"
              height="480"
              loading="eager"
              fetchpriority="high"
              decoding="async" />
            
          </button>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) =>
              link.dropdown ? (
                <Dropdown key={link.label} label={link.label} items={link.dropdown} scrolled={scrolled} />
              ) : (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className={`text-base font-medium tracking-widest-xl transition-colors duration-300 hover:opacity-60 ${
                    link.label === "LÆR MER" ? "text-white bg-[#3d7a54] px-3 py-1 rounded-sm" : (scrolled ? "text-foreground" : "text-foreground")
                  }`}
                >
                  {link.label}
                </button>
              )
            )}
          </div>

          <SearchBar scrolled={scrolled} />

          <button
            onClick={() => setContactModalOpen(true)}
            className={`hidden lg:block text-sm tracking-widest-xl font-body font-medium px-6 py-2.5 border transition-all duration-300 hover:bg-foreground hover:text-background ml-4 ${
            scrolled ?
            "border-foreground/30 text-foreground" :
            "border-foreground/30 text-foreground"}`
            }>
            
            KONTAKT
          </button>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Lukk meny" : "Åpne meny"}
            aria-expanded={mobileOpen}
            className={`lg:hidden transition-colors ${
            scrolled ? "text-foreground" : "text-foreground"}`}
          >
            
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`fixed inset-0 z-40 bg-background transition-all duration-500 flex flex-col items-center justify-center gap-8 ${
        mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      >
        
        {navLinks.map((link) =>
        <div key={link.label} className="flex flex-col items-center gap-4">
          {link.dropdown ? (
            <>
              <button
                onClick={() => setExpandedDropdown(expandedDropdown === link.label ? null : link.label)}
                className="font-heading text-2xl tracking-widest-xl text-foreground hover:text-primary transition-colors">
                {link.label}
              </button>
              {expandedDropdown === link.label && (
                <div className="flex flex-col gap-3">
                  {link.dropdown.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => handleNavClick(item.href)}
                      className="font-heading text-lg tracking-widest-xl text-primary hover:text-foreground transition-colors">
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <button
              onClick={() => handleNavClick(link.href)}
              className={`font-heading text-2xl tracking-widest-xl transition-colors ${
                link.label === "LÆR MER" ? "text-white bg-[#3d7a54] hover:bg-[#2d5a3d] px-4 py-1.5 rounded-sm" : "text-foreground hover:text-primary"
              }`}
            >
              {link.label}
            </button>
          )}
        </div>
        )}
        <button
          onClick={() => { setMobileOpen(false); setContactModalOpen(true); }}
          className="mt-4 text-sm tracking-widest-xl font-body font-medium px-8 py-3 border border-foreground/30 text-foreground hover:bg-foreground hover:text-background transition-all">
          
          KONTAKT
        </button>
      </div>
      <ContactModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} />
    </>);

}